import { zodResolver } from '@hookform/resolvers/zod';
import type { TenantChoice } from '@rfleet/shared';
import { Building2, Eye, EyeOff, Globe, Lock, Mail, ShieldCheck, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate, Link } from 'react-router-dom';
import { BrandMark } from '../../components/brand/BrandMark';
import { Spinner } from '../../components/ui';
import { ApiError } from '../../lib/api';
import { useSession } from '../../lib/session';
import { AuthBackground } from './AuthBackground';

import { z } from 'zod';

const LoginFormSchema = z.object({
  tenantSlug: z
    .string()
    .optional()
    .transform((v) => (v && v.trim() !== '' ? v.trim() : undefined))
    .refine((v) => !v || (v.length >= 2 && v.length <= 40 && /^[a-z0-9][a-z0-9-]*$/.test(v)), {
      message: 'slug must be lowercase letters, digits and dashes',
    }),
  email: z.string().email().max(120).transform((s) => s.trim().toLowerCase()),
  password: z.string().min(8).max(128),
});

type LoginFormData = z.infer<typeof LoginFormSchema>;

export function LoginPage() {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const loc = useLocation();
  const { status, loginStaff } = useSession();

  const [tenants, setTenants] = useState<TenantChoice[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showDemoHint, setShowDemoHint] = useState(false);

  const form = useForm<LoginFormData>({
    resolver: zodResolver(LoginFormSchema),
    defaultValues: { email: '', password: '', tenantSlug: undefined },
  });

  if (status === 'authenticated') {
    return <Navigate to={(loc.state as { from?: string })?.from ?? '/'} replace />;
  }

  const onSubmit = form.handleSubmit(async (dto) => {
    setError(null);
    try {
      const res = await loginStaff({ ...dto, tenantSlug: dto.tenantSlug || undefined });
      if ('tenantRequired' in res) {
        setTenants(res.tenants);
        return;
      }
      nav((loc.state as { from?: string })?.from ?? '/', { replace: true });
    } catch (e) {
      setError(e instanceof ApiError ? e.localized : t('common.error'));
    }
  });

  const fillDemo = (email: string, pass: string, slug?: string) => {
    form.setValue('email', email, { shouldValidate: true });
    form.setValue('password', pass, { shouldValidate: true });
    if (slug) {
      form.setValue('tenantSlug', slug, { shouldValidate: true });
    } else {
      form.setValue('tenantSlug', undefined, { shouldValidate: true });
    }
    setError(null);
  };

  const isRtl = i18n.language === 'ar';

  return (
    <main className="relative flex min-h-dvh flex-col items-center justify-center p-4 sm:p-6 overflow-hidden">
      {/* Animated Telemetry & Radar Background */}
      <AuthBackground />

      {/* Top Bar with Language Switcher and Live Telemetry Status */}
      <header className="absolute top-4 sm:top-6 inset-x-4 sm:inset-x-8 flex items-center justify-between z-10">
        <div className="inline-flex items-center gap-2 rounded-full border border-[#BEF264]/25 bg-[#0A2216]/80 px-3.5 py-1.5 text-xs font-medium text-[#cde7d3] shadow-lg backdrop-blur-md">
          <span className="relative flex size-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#BEF264] opacity-75" />
            <span className="relative inline-flex size-2 rounded-full bg-[#BEF264]" />
          </span>
          <span>{isRtl ? 'نظام التتبع المباشر نشط' : 'Live Fleet Telemetry Active'}</span>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/landing"
            className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-semibold text-white shadow-lg backdrop-blur-md transition-all hover:bg-white/20 hover:border-[#BEF264]/40"
          >
            <span>{isRtl ? '← الرئيسية' : '← Home'}</span>
          </Link>

          <button
            type="button"
            onClick={() => void i18n.changeLanguage(isRtl ? 'en' : 'ar')}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3.5 py-1.5 text-xs font-semibold text-white shadow-lg backdrop-blur-md transition-all hover:bg-white/20 hover:border-[#BEF264]/40 cursor-pointer"
            title={isRtl ? 'Switch to English' : 'التحويل إلى العربية'}
          >
            <Globe size={14} className="text-[#BEF264]" aria-hidden="true" />
            <span>{isRtl ? 'English' : 'العربية'}</span>
          </button>
        </div>
      </header>

      {/* Main Glassmorphic Login Card */}
      <div className="relative z-10 w-full max-w-[440px]">
        {/* Subtle Ambient Glow behind the card */}
        <div className="absolute -inset-1 rounded-[32px] bg-gradient-to-b from-[#BEF264]/25 via-transparent to-[#0F3D26]/40 blur-xl -z-10" />

        <div className="rounded-[28px] border border-white/15 bg-[#0c2417]/85 p-6 sm:p-9 shadow-[0_25px_60px_rgba(0,0,0,0.6)] backdrop-blur-2xl">
          {/* Header & Logo */}
          <div className="mb-7 flex flex-col items-center text-center">
            <div className="mb-3 transition-transform duration-300 hover:scale-105 drop-shadow-[0_0_20px_rgba(190,242,100,0.45)]">
              <BrandMark size={52} />
            </div>

            <h1 className="text-2xl font-bold tracking-tight text-white flex items-center gap-1.5">
              <span>RFleet</span>
            </h1>
            <p className="mt-1 text-xs text-[#9dc5a7] max-w-[280px]">
              {isRtl ? 'منصة إدارة وتتبع المركبات الذكية' : 'Intelligent Fleet Management & Telemetry'}
            </p>
          </div>

          {/* Form */}
          <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
            {/* Multi-Tenant Selection (when email belongs to multiple tenants) */}
            {tenants ? (
              <div className="flex flex-col gap-1.5 animate-fadeIn">
                <label htmlFor="login-tenant" className="text-xs font-medium text-[#cde7d3] flex items-center gap-1.5">
                  <Building2 size={13} className="text-[#BEF264]" />
                  <span>{t('login.tenant')}</span>
                  <span className="text-red-400">*</span>
                </label>
                <div className="relative">
                  <select
                    id="login-tenant"
                    className="w-full appearance-none rounded-xl border border-white/15 bg-white/[0.07] px-3.5 py-2.5 text-sm text-white focus:border-[#BEF264] focus:bg-white/[0.12] focus:outline-none focus:ring-1 focus:ring-[#BEF264] transition-all cursor-pointer"
                    {...form.register('tenantSlug')}
                  >
                    <option value="" className="bg-[#0c2417] text-white">— {isRtl ? 'اختر الشركة' : 'Select Company'} —</option>
                    {tenants.map((x) => (
                      <option key={x.slug} value={x.slug} className="bg-[#0c2417] text-white">
                        {isRtl ? (x.nameAr || x.nameEn) : (x.nameEn || x.nameAr)} ({x.slug})
                      </option>
                    ))}
                  </select>
                </div>
                {form.formState.errors.tenantSlug?.message ? (
                  <p className="text-xs text-red-400 mt-0.5">{form.formState.errors.tenantSlug.message}</p>
                ) : (
                  <p className="text-[11px] text-[#8aa093]">{t('login.tenantHint')}</p>
                )}
              </div>
            ) : null}

            {/* Email Field */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="login-email" className="text-xs font-medium text-[#cde7d3] flex items-center gap-1.5">
                <Mail size={13} className="text-[#BEF264]" />
                <span>{t('login.email')}</span>
                <span className="text-red-400">*</span>
              </label>
              <div className="relative" dir="ltr">
                <input
                  id="login-email"
                  type="email"
                  autoComplete="username"
                  placeholder="name@company.com"
                  className="w-full rounded-xl border border-white/15 bg-white/[0.07] px-3.5 py-2.5 text-sm text-white placeholder:text-[#6f8f7b] focus:border-[#BEF264] focus:bg-white/[0.12] focus:outline-none focus:ring-1 focus:ring-[#BEF264] transition-all text-left"
                  {...form.register('email')}
                />
              </div>
              {form.formState.errors.email?.message ? (
                <p className="text-xs text-red-400 mt-0.5">{form.formState.errors.email.message}</p>
              ) : null}
            </div>

            {/* Password Field */}
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <label htmlFor="login-password" className="text-xs font-medium text-[#cde7d3] flex items-center gap-1.5">
                  <Lock size={13} className="text-[#BEF264]" />
                  <span>{t('login.password')}</span>
                  <span className="text-red-400">*</span>
                </label>
              </div>
              <div className="relative" dir="ltr">
                <input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="w-full rounded-xl border border-white/15 bg-white/[0.07] px-3.5 py-2.5 pr-10 text-sm text-white placeholder:text-[#6f8f7b] focus:border-[#BEF264] focus:bg-white/[0.12] focus:outline-none focus:ring-1 focus:ring-[#BEF264] transition-all text-left"
                  {...form.register('password')}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-[#9dc5a7] hover:text-white transition-colors cursor-pointer"
                  title={showPassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {form.formState.errors.password?.message ? (
                <p className="text-xs text-red-400 mt-0.5">{form.formState.errors.password.message}</p>
              ) : null}
            </div>

            {/* Error Banner */}
            {error ? (
              <div className="rounded-xl border border-red-500/30 bg-red-500/15 p-3 text-xs text-red-200" role="alert">
                {error}
              </div>
            ) : null}

            {/* Submit CTA Button */}
            <button
              type="submit"
              disabled={form.formState.isSubmitting}
              className="mt-2 min-h-11 w-full rounded-xl bg-[#BEF264] text-[#0F3D26] font-bold text-sm hover:bg-[#cbf777] hover:shadow-[0_0_25px_rgba(190,242,100,0.5)] active:scale-[0.99] transition-all duration-150 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {form.formState.isSubmitting ? <Spinner className="text-[#0F3D26]" /> : null}
              <span>{t('login.submit')}</span>
            </button>
          </form>

          {/* Quick Demo Helper (Collapsible) */}
          <div className="mt-5 pt-4 border-t border-white/10">
            <button
              type="button"
              onClick={() => setShowDemoHint(!showDemoHint)}
              className="w-full flex items-center justify-between text-xs text-[#9dc5a7] hover:text-[#BEF264] transition-colors py-1 cursor-pointer"
            >
              <span className="flex items-center gap-1.5">
                <Sparkles size={12} className="text-[#BEF264]" />
                <span>{isRtl ? 'حسابات التجربة السريعة (Demo)' : 'Quick Demo Logins'}</span>
              </span>
              <span className="text-[10px]">{showDemoHint ? '▲' : '▼'}</span>
            </button>

            {showDemoHint ? (
              <div className="mt-2.5 flex flex-col gap-1.5 text-xs text-[#cde7d3] bg-white/[0.04] border border-white/10 rounded-xl p-2.5 animate-fadeIn">
                <button
                  type="button"
                  onClick={() => fillDemo('admin@demo.rfleet.local', 'Admin#12345', 'demo')}
                  className="w-full text-start p-1.5 rounded-lg hover:bg-white/10 transition-colors flex items-center justify-between cursor-pointer"
                >
                  <div>
                    <div className="font-semibold text-white">{isRtl ? 'مدير شركة تجريبية' : 'Tenant Admin'}</div>
                    <div className="text-[11px] text-[#9dc5a7]">admin@demo.rfleet.local</div>
                  </div>
                  <span className="text-[11px] font-mono text-[#BEF264] bg-[#BEF264]/15 px-1.5 py-0.5 rounded">
                    {isRtl ? 'ملء تلقائي' : 'Fill'}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => fillDemo('super@rfleet.local', 'Super#12345')}
                  className="w-full text-start p-1.5 rounded-lg hover:bg-white/10 transition-colors flex items-center justify-between cursor-pointer"
                >
                  <div>
                    <div className="font-semibold text-white">{isRtl ? 'مدير المنصة العام' : 'Super Admin'}</div>
                    <div className="text-[11px] text-[#9dc5a7]">super@rfleet.local</div>
                  </div>
                  <span className="text-[11px] font-mono text-[#BEF264] bg-[#BEF264]/15 px-1.5 py-0.5 rounded">
                    {isRtl ? 'ملء تلقائي' : 'Fill'}
                  </span>
                </button>
              </div>
            ) : null}
          </div>
        </div>

        {/* Security & Telemetry Footer Note */}
        <div className="mt-4 flex items-center justify-center gap-1.5 text-xs text-[#72927e]">
          <ShieldCheck size={14} className="text-[#BEF264]" />
          <span>{isRtl ? 'نظام مشفر ومحمي ببروتوكولات الأمان السحابية' : 'End-to-End Encrypted Fleet Security'}</span>
        </div>
      </div>
    </main>
  );
}
